import { type ReactNode } from 'react';
import { useNavigate } from 'react-router';

import { useTranslation } from 'react-i18next';
import { Maximize2 } from 'lucide-react';

import { ROUTES } from '@/router/routes';
import { ROLES } from '@/utils/constants';
import { useUserProfile } from '@/states/user-profile/use-user-profile';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';

export interface DashboardModuleProps {
  title: string;
  description?: string;
  children: ReactNode;
  fullscreenRoute?: (typeof ROUTES)[keyof typeof ROUTES];
  headerActions?: ReactNode;
  roles?: ROLES[];
  className?: string;
}

export function DashboardModule({
  title,
  description,
  children,
  fullscreenRoute,
  headerActions,
  roles,
  className,
}: DashboardModuleProps) {
  const { profile } = useUserProfile();
  const navigate = useNavigate();
  const { t } = useTranslation();

  if (roles && (!profile.role || !roles.includes(profile.role))) {
    return null;
  }

  return (
    <Card className={`flex flex-col max-h-[500px] ${className ?? ''}`}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 shrink-0">
        <div>
          <h2 className="font-semibold leading-none">{title}</h2>
          {description && <CardDescription>{description}</CardDescription>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {headerActions}
          {fullscreenRoute && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(fullscreenRoute)}
              className="h-8 w-8"
              aria-label={t('dashboard-module-view-fullscreen')}
            >
              <Maximize2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="flex-1 overflow-auto">{children}</CardContent>
    </Card>
  );
}
